import { useState, useEffect } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";

// Argon Dashboard components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonInput from "components/ArgonInput";
import ArgonButton from "components/ArgonButton";

// Argon Dashboard example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";

// Auth context
import { useAuth } from "contexts/AuthContext";

// Avatar options
const avatarOptions = [
  { id: 1, color: "#4CAF50", icon: "person" },
  { id: 2, color: "#2196F3", icon: "business" },
  { id: 3, color: "#FF9800", icon: "home" },
  { id: 4, color: "#9C27B0", icon: "apartment" },
  { id: 5, color: "#F44336", icon: "villa" },
  { id: 6, color: "#00BCD4", icon: "house" },
];

function UserProfile() {
  const { user, updateUser } = useAuth();
  
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    company: "",
    current_password: "",
    new_password: "",
    confirm_password: "",
    avatarId: 1
  });
  
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [passwordMode, setPasswordMode] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        full_name: user.full_name || "",
        email: user.email || "",
        phone: user.phone || "",
        company: user.company || "",
        avatarId: user.avatarId || 1
      }));
    }
  }, [user]);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: "", text: "" });

    try {
      const updateData = {
        full_name: formData.full_name,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        avatarId: formData.avatarId
      };

      const result = await updateUser(updateData);
      if (result.success) {
        setMessage({ type: "success", text: "Profile updated successfully!" });
      } else {
        setMessage({ type: "error", text: result.error || "Failed to update profile. Please check your information and try again." });
      }
    } catch (error) {
      console.error("Profile update error:", error);
      setMessage({ 
        type: "error", 
        text: error.response?.data?.detail || "Unable to update profile. Please check your internet connection and try again." 
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: "", text: "" });

    if (formData.new_password !== formData.confirm_password) {
      setMessage({ type: "error", text: "New passwords don't match. Please make sure both password fields are identical." });
      setLoading(false);
      return;
    }

    if (formData.new_password.length < 8) {
      setMessage({ type: "error", text: "Password must be at least 8 characters long for security." });
      setLoading(false);
      return;
    }

    if (!formData.current_password) {
      setMessage({ type: "error", text: "Please enter your current password to verify your identity." });
      setLoading(false);
      return;
    }

    try {
      const updateData = {
        current_password: formData.current_password,
        password: formData.new_password
      };

      const result = await updateUser(updateData);
      if (result.success) {
        setMessage({ type: "success", text: "Password updated successfully!" });
        setFormData(prev => ({
          ...prev,
          current_password: "",
          new_password: "",
          confirm_password: ""
        }));
        setPasswordMode(false);
      } else {
        const errorMsg = result.error || "Failed to update password.";
        if (errorMsg.toLowerCase().includes("current password")) {
          setMessage({ type: "error", text: "Current password is incorrect. Please try again." });
        } else if (errorMsg.toLowerCase().includes("weak")) {
          setMessage({ type: "error", text: "New password is too weak. Use at least 8 characters with a mix of letters, numbers, and symbols." });
        } else {
          setMessage({ type: "error", text: errorMsg });
        }
      }
    } catch (error) {
      console.error("Profile update error:", error);
      setMessage({ 
        type: "error", 
        text: error.response?.data?.detail || "Unable to update profile. Please check your internet connection and try again." 
      });
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <DashboardLayout>
        <ArgonBox p={3}>
          <ArgonTypography variant="h6">Loading...</ArgonTypography>
        </ArgonBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <ArgonBox mt={4}>
        <Grid container spacing={3}>
          {/* Avatar Selection Card */}
          <Grid item xs={12}>
            <Card>
              <ArgonBox p={3}>
                <ArgonTypography variant="h6" mb={3}>
                  Profile Avatar
                </ArgonTypography>
                <ArgonBox display="flex" gap={2} flexWrap="wrap" justifyContent="center">
                  {avatarOptions.map((avatar) => (
                    <IconButton
                      key={avatar.id}
                      onClick={() => setFormData(prev => ({ ...prev, avatarId: avatar.id }))}
                      sx={{
                        p: 0.5,
                        border: formData.avatarId === avatar.id ? "3px solid" : "1px solid",
                        borderColor: formData.avatarId === avatar.id ? "primary.main" : "grey.300"
                      }}
                    >
                      <Avatar
                        sx={{
                          bgcolor: avatar.color,
                          width: 56,
                          height: 56
                        }}
                      >
                        <Icon fontSize="large">{avatar.icon}</Icon>
                      </Avatar>
                    </IconButton>
                  ))}
                </ArgonBox>
              </ArgonBox>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={8}>
            <Card>
              <ArgonBox p={3}>
                <ArgonTypography variant="h6" mb={3}>
                  Profile Information
                </ArgonTypography>
                
                {message.text && (
                  <ArgonBox mb={2}>
                    <Alert severity={message.type}>{message.text}</Alert>
                  </ArgonBox>
                )}

                <ArgonBox component="form" onSubmit={handleProfileUpdate}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <ArgonInput
                        name="full_name"
                        placeholder="Full Name"
                        value={formData.full_name}
                        onChange={handleChange}
                        size="large"
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <ArgonInput
                        name="email"
                        type="email"
                        placeholder="Email Address"
                        value={formData.email}
                        onChange={handleChange}
                        size="large"
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <ArgonInput
                        name="phone"
                        placeholder="Phone Number"
                        value={formData.phone}
                        onChange={handleChange}
                        size="large"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <ArgonInput
                        name="company"
                        placeholder="Company"
                        value={formData.company}
                        onChange={handleChange}
                        size="large"
                      />
                    </Grid>
                  </Grid>
                  
                  <ArgonBox mt={3} mb={1}>
                    <ArgonButton
                      type="submit"
                      color="info"
                      size="large"
                      disabled={loading}
                    >
                      {loading ? "Updating..." : "Update Profile"}
                    </ArgonButton>
                  </ArgonBox>
                </ArgonBox>
              </ArgonBox>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <ArgonBox p={3}>
                <ArgonTypography variant="h6" mb={3}>
                  Account Security
                </ArgonTypography>
                
                {!passwordMode ? (
                  <ArgonBox>
                    <ArgonTypography variant="body2" color="text" mb={2}>
                      Keep your account secure by updating your password regularly.
                    </ArgonTypography>
                    <ArgonButton
                      color="warning"
                      variant="outlined"
                      size="small"
                      onClick={() => setPasswordMode(true)}
                    >
                      Change Password
                    </ArgonButton>
                  </ArgonBox>
                ) : (
                  <ArgonBox component="form" onSubmit={handlePasswordUpdate}>
                    <ArgonBox mb={2}>
                      <ArgonInput
                        name="current_password"
                        type="password"
                        placeholder="Current Password"
                        value={formData.current_password}
                        onChange={handleChange}
                        size="large"
                        required
                      />
                    </ArgonBox>
                    <ArgonBox mb={2}>
                      <ArgonInput
                        name="new_password"
                        type="password"
                        placeholder="New Password"
                        value={formData.new_password}
                        onChange={handleChange}
                        size="large"
                        required
                      />
                    </ArgonBox>
                    <ArgonBox mb={3}>
                      <ArgonInput
                        name="confirm_password"
                        type="password"
                        placeholder="Confirm New Password"
                        value={formData.confirm_password}
                        onChange={handleChange}
                        size="large"
                        required
                      />
                    </ArgonBox>
                    
                    <ArgonBox display="flex" gap={1}>
                      <ArgonButton
                        type="submit"
                        color="warning"
                        size="small"
                        disabled={loading}
                      >
                        {loading ? "Updating..." : "Update Password"}
                      </ArgonButton>
                      <ArgonButton
                        color="secondary"
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          setPasswordMode(false);
                          setFormData(prev => ({
                            ...prev,
                            current_password: "",
                            new_password: "",
                            confirm_password: ""
                          }));
                        }}
                      >
                        Cancel
                      </ArgonButton>
                    </ArgonBox>
                  </ArgonBox>
                )}
              </ArgonBox>
            </Card>

          </Grid>
        </Grid>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default UserProfile;